import { pathOr, compose, ifElse, isEmpty, always } from 'ramda';
import { APP_CONSTANT, MED_CONSTANT } from 'src/constants';
import { capitalizeFirstLetter } from 'src/utils/common.util';

const CONTACT_FIELD_NAME_FIRST = `${APP_CONSTANT.APPOINTMENT_BOOKING}.name_first`;
const CONTACT_FIELD_NAME_LAST = `${APP_CONSTANT.APPOINTMENT_BOOKING}.name_last`;
const CONTACT_FIELD_NAME_MIDDLE = `${APP_CONSTANT.APPOINTMENT_BOOKING}.name_middle`;
const CONTACT_FIELD_ADDRESS = `${APP_CONSTANT.APPOINTMENT_BOOKING}.address`;
const CONTACT_FIELD_PHONE = `${APP_CONSTANT.APPOINTMENT_BOOKING}.number`;
const CONTACT_FIELD_BIRTHDATE = `${APP_CONSTANT.APPOINTMENT_BOOKING}.birthdate`;

const CONTACT_FIELDS = [
  CONTACT_FIELD_NAME_FIRST,
  CONTACT_FIELD_NAME_LAST,
  CONTACT_FIELD_NAME_MIDDLE,
  CONTACT_FIELD_ADDRESS,
  CONTACT_FIELD_PHONE,
  CONTACT_FIELD_BIRTHDATE
];

const isBasicField = (field) => field.is_basic;

const getBasicFieldType = (field) => {
  if (!isBasicField(field)) return;
  // name_first, name_middle, name_last , birthdate, ssn type
  if (field.legwork_table_name === 'contact') {
    if (field.legwork_column_name === 'gender') {
      return 'sex';
    }

    return field.legwork_column_name;
  }

  // email type
  if (
    field.legwork_table_name === 'contact_email' &&
    field.legwork_column_name === 'address'
  ) {
    return 'email';
  }

  if (
    field.legwork_table_name === 'contact_phone' &&
    field.legwork_column_name === 'number'
  ) {
    return 'primary_phone';
  }

  // address, address2, city, state, zip
  if (
    field.legwork_table_name === 'contact_postal_address' &&
    field.legwork_column_name === 'postal_address_object'
  ) {
    switch (field.display_name) {
      case 'Address 1':
        return 'address';
      case 'Address 2':
        return 'address2';
      case 'City':
        return 'city';
      case 'State / Province':
        return 'state';
      case 'Zip / Postal Code':
        return 'zip';

      default:
        return null;
    }
  }

  return null;
};

const LIST_BASIC_FIELD_TYPE = [
  'name_first',
  'name_middle',
  'name_last',
  'birthdate',
  'email',
  'primary_phone',
  'ssn',
  'sex',
  'address',
  'address2',
  'city',
  'state',
  'zip'
];

const PATIENT_FORMS = [
  'Patient Information',
  'Communication Release',
  'Medical History',
  'Insurance Information',
  'Authorizations and Acknowledgments ',
  'Dental History'
];

const canPrepopulateInInitialPatientForm = (fieldUuid, formName) =>
  PATIENT_FORMS.includes(formName) &&
  LIST_PREPOPULATE_FIELDS_FOR_PATIENT_FORMS.hasOwnProperty(fieldUuid);

const canPrepopulateInOtherForms = (field, formName) =>
  !PATIENT_FORMS.includes(formName) && isBasicField(field);

const getAlertEntries = (userInfo, options) => {
  const alerts = pathOr({}, ['medical'])(
    userInfo
  );

  return Object.entries(alerts)
    .filter(([key, value]) => options.includes(key) && value === 'true')
    .map(([key, value]) => {
      return { [key]: value };
    });
}

const LIST_PREPOPULATE_FIELDS_FOR_PATIENT_FORMS = {
  // medical history
  '3b23676c-3655-489a-b062-2f55c484e65b': { type: 'name_first' },
  '44a1377a-30db-4243-be4c-7ae2370ead49': { type: 'name_middle' },
  'e15c99c8-a58f-49f6-b009-0c520dbd6db0': { type: 'name_last' },
  'ee8ed8d8-6c62-4404-9b49-1dc7abe45214': { type: 'email' },
  'f6f62a61-af49-436f-a7aa-2181244cf709': { type: 'primary_phone' },

  // communication release
  '754a7511-0397-4b79-a818-016b224eb632': { type: 'name_first' },
  '61230bc7-2433-4026-9ed4-5bf3315c237a': { type: 'name_middle' },
  'ee1d1cd0-b6a3-4b9d-8a8a-3edcffe12898': { type: 'name_last' },
  '75a85810-1bef-4246-b104-0282654e83c0': { type: 'email' },
  'a2510424-147f-4569-b2b1-136c33f35afc': { type: 'primary_phone' },
  'ecd00dc8-6f89-4a83-87c3-e28780146daf': {
    type: 'communication_type_preference'
  },

  // authorization and acknowledgements
  '5b17bd10-6844-4a9e-b955-faf2cf6c07d9': { type: 'name_first' },
  'ff95f448-56dc-4e4b-82a2-17a4aff914c1': { type: 'name_middle' },
  '5030837a-e07c-42b0-88f6-1203777c01d2': { type: 'name_last' },
  '63d3ad50-b58f-4aaf-969c-6490d502248b': { type: 'email' },
  '703a1267-c193-41c7-bc0a-f99702677831': { type: 'primary_phone' },

  // insurance_info
  'df31fdf8-91ce-4f65-8024-be4f2acf7d75': { type: 'name_first' },
  '4f584f9e-7fee-4761-9971-aec4c163b6c0': { type: 'name_middle' },
  '49d07361-0228-44ae-8ee3-85abbae42db6': { type: 'name_last' },
  '39bd90c5-1865-4626-afb6-30b61d2c1f27': { type: 'email' },
  'c46c6b59-d98b-45b2-9987-cdeb62f41819': { type: 'primary_phone' },

  // dental_history
  'ed7007a8-f083-4a8d-9fcd-5625a5e47acc': { type: 'name_first' },
  '2cf46485-0b9e-40eb-9be4-b0d200dd4916': { type: 'name_middle' },
  '6ad457b4-4b6f-4878-bf48-9f4b5cc57716': { type: 'name_last' },
  '6b76ca5f-cd96-4fcd-bc15-299e2128afc5': { type: 'email' },
  'c7214153-1054-4974-b741-360bbbe72c1c': { type: 'primary_phone' },

  // patient_history
  '085ab763-d786-41e6-ae37-d16b685a33ce': { type: 'name_first' },
  'ca6f7058-8539-48c1-b01a-50d7f04a02a0': { type: 'name_middle' },
  '795cdc82-3117-42ad-861e-770305f31067': { type: 'name_last' },
  'd2f23264-e8c1-40ef-9103-fe3ba8b3dbf3': { type: 'birthdate' },
  '06765f75-30a8-4819-a0e6-4fa9e6693370': { type: 'email' },
  '878b93bd-d79e-46a8-84bf-c9502499f0d1': { type: 'primary_phone' },
  '7883b01d-955f-4d0e-8397-cdc5b8fc70cd': { type: 'alt_phone' },
  '8f968900-527a-44d2-a35a-720ebf695ec7': { type: 'ssn' },
  '5baf98fe-fdcb-409a-95be-9b2de3e77a2d': { type: 'sex' },
  '68c766c5-3bbc-46fc-a11b-c30bcb4bfb2e': { type: 'address' },
  'b9f88905-af95-49d8-b7b6-8a86e60f2a97': { type: 'address2' },
  '2c41f5f4-6588-49eb-84f8-a3bcfc31d259': { type: 'city' },
  '5b5308e6-e9b3-4db0-af92-747bc2cf1640': { type: 'state' },
  '53e07b64-e1d1-4d5b-95d3-6b2e46bd4638': { type: 'zip' },
  'e87dc676-59d3-4ee7-a73b-4a38f33b7cbf': { type: 'marital_status' },
  '4c91d83c-b096-431e-8060-7491ec97e6ea': { type: 'spouse_first_name' },
  '8967a6e3-065b-47bf-8ff7-6f791da04595': { type: 'spouse_middle_name' },
  '30038ac9-94e8-47e1-8b30-2d614fe51e77': { type: 'spouse_last_name' },
  '3f032e8e-6525-4914-bbaf-10cf27ad02ee': { type: 'spouse_birthdate' },
  '3559fe67-cc5d-4fcd-ad78-0ea5fefb64b1': { type: 'spouse_ssn' },
  '59593c9d-49a6-40e1-aa82-f3426b13f710': { type: 'spouse_phone' },
  'dce69b6a-5c31-46b0-8973-75608fa3bf24': { type: 'guardian_1_first_name' },
  'a18760fa-6ae1-419d-be5d-1f7bda0ab891': { type: 'guardian_1_middle_name' },
  '54014a13-ff84-4347-b4ea-393cd2d8e047': { type: 'guardian_1_last_name' },
  'ad5e8aea-a0c8-48de-a452-3b8f756d24a4': { type: 'guardian_1_relationship' },
  '527a287c-cd31-4495-9c6b-96bde234ba1a': { type: 'guardian_1_birthdate' },
  '46c6f191-3a99-4aed-a0f6-43bfc2bca6dd': { type: 'guardian_1_ssn' },
  '55c98205-fc3b-4fd6-be4a-837776cc6536': { type: 'guardian_1_sex' },
  '39d5f718-fb2b-4eea-b0ec-6cd8161af0fa': { type: 'guardian_1_email' },
  '20ea4961-48d8-4b2e-9cbb-c5199da24864': { type: 'guardian_1_primary_phone' },
  'ce46564c-e995-4fea-bc63-479ebb7cd2c3': { type: 'guardian_1_alt_phone' },
  'faa22561-0239-48dc-bf6a-da53e8ff8f81': { type: 'guardian_1_address' },
  '04eec8fc-9457-458f-81cd-49aebad416e7': { type: 'guardian_1_address2' },
  'f6cec9af-3dc9-4614-9c6d-88deccb50ba9': { type: 'guardian_1_city' },
  'c601ea2f-f025-4a56-a386-d8782b552ca0': { type: 'guardian_1_state' },
  'd209f434-caec-4f1e-b5ad-1f4085530cbe': { type: 'guardian_1_zip' },
  '1682c597-8272-4791-8c72-4446f71a9fa7': { type: 'guardian_1_country' },
  '4face656-3e74-48a4-a8c4-e8e5a14ec14c': { type: 'guardian_2_first_name' },
  '716a0c08-7541-4362-802b-2c3f98e5b880': { type: 'guardian_2_middle_name' },
  'a7cc0d64-b0b7-46c1-8d35-6ea589b9589c': { type: 'guardian_2_last_name' },
  'bd16a8ab-6bb0-4618-a675-b12121604ff0': { type: 'guardian_2_relationship' },
  '2d84d279-d7e0-4190-ad4e-51ad567ba83e': { type: 'guardian_2_birthdate' },
  '1b684b9d-6d72-4de6-8c09-8ece9a5387f6': { type: 'guardian_2_ssn' },
  '2ccebf97-08af-4ce2-b8a0-c00d375dacdd': { type: 'guardian_2_sex' },
  '7779cc4a-479b-4352-8d26-be094c5fd67a': { type: 'guardian_2_email' },
  '72134459-b5c1-431f-b816-05387977e476': { type: 'guardian_2_primary_phone' },
  '79560b63-d8f4-4f4d-8f94-4238ca8ce0ba': { type: 'guardian_2_alt_phone' },
  'f616ee39-6dd8-4e67-9982-d9f3c58020ac': { type: 'guardian_2_address' },
  '7e0f8f11-4b55-475b-b514-0c98ef0af22e': { type: 'guardian_2_address2' },
  '406dacbb-b6ff-4e31-9968-ca17df3a82de': { type: 'guardian_2_city' },
  '156bc900-ae64-436f-b0c7-b5e95de7ca03': { type: 'guardian_2_state' },
  'b0b0d13a-71f6-4c17-8297-64397701ae46': { type: 'guardian_2_zip' },
  '2499b0b9-b001-4863-93bc-45a090ff059a': { type: 'guardian_2_country' },
  'fcf1a86b-fa7b-4a59-9c90-6afd9d345d30': {
    type: 'billing_party_relationship_to_patient'
  },
  '9fe4fe52-2caf-4605-94df-bb7ef2a40ce5': { type: 'billing_first_name' },
  'a96fc915-2509-4554-91e6-f1c970cee478': { type: 'billing_middle_name' },
  'b08ad553-c334-46ca-bc36-df7d7c986aac': { type: 'billing_last_name' },
  'ce2005d9-69c5-47fc-93cb-5ef25556958b': { type: 'billing_birthdate' },
  'de7fd649-1b06-498d-99e0-2f241124ba79': { type: 'billing_ssn' },
  '0d855ab8-d300-47f0-bd6f-ba7d64740790': { type: 'billing_sex' },
  '4c8e6baa-ff92-498b-94ab-b52e29419e02': { type: 'billing_email' },
  'a61506e7-3017-4c49-92c1-ff9c6eb91196': { type: 'billing_address' },
  '77fe63cc-1861-4e46-8ecc-10555b155bd6': { type: 'billing_address2' },
  'dbf980a8-e854-486a-95aa-6c05c8f5f766': { type: 'billing_city' },
  '418fe2f6-47c3-466c-8cc1-796714ae3f17': { type: 'billing_state' },
  '2a2335c7-8a47-4d16-8f05-dac323e29efd': { type: 'billing_zip' },
  'f1dd81ab-fc5a-4c35-a804-b59718a3df52': { type: 'billing_country' },
  '557484c7-34a5-47fc-890c-8177cf6b5fe3': { type: 'billing_primary_phone' },
  '757ee8b4-cec8-470a-a266-2beb585e950a': { type: 'billing_alt_phone' },
  '5ce63b24-eb58-47a6-b243-ccc7fcd686fc': { type: 'how_did_you_hear_about_us' },
  '6e4e3771-03d9-4f61-a2e0-1db6493e6466': {
    type: 'primary_insured_first_name'
  },
  '4fc3fd5a-8d1a-4e3d-ad97-e4f77cfa3748': { type: 'primary_insured_last_name' },
  '20c92595-7959-48a8-9bdc-779198192eb4': { type: 'primary_insured_birthdate' },
  '94da3801-f5fa-4ee5-90b9-6af0848eae8c': { type: 'primary_insured_ssn' },
  '5ec99809-cc81-48a9-8de2-fdfb92259022': {
    type: 'primary_insurance_company_name'
  },
  'd1c1f9e5-a035-4cb8-8f19-a5f63fd35320': {
    type: 'primary_insurance_company_phone_number'
  },
  'b7f21fae-0acc-4dfa-8db7-ba1b2ab2e8cf': {
    type: 'primary_insurance_company_policy_number'
  },
  'dc46abe6-4473-40c9-a32f-5de8b76de2a3': {
    type: 'primary_insurance_company_group_number'
  },
  'b58feb03-6f2d-4014-9dbe-8a94ad883c47': {
    type: 'primary_insurance_company_address' 
  },
  '18edf476-8a05-472a-8ba1-327bbf133214': {
    type: 'primary_insurance_company_city'
  },
  '1dfd9657-1910-4b4a-a08c-b5d4b803ec50': {
    type: 'primary_insurance_company_state'
  },
  '0d3d3e71-b855-4cc0-adbf-299494f65c60': {
    type: 'primary_insurance_company_zip'
  },
  'ca1d66d1-114c-464c-a0ce-6419cdc9f855': {
    type: 'secondary_insured_first_name'
  },
  '70b05f89-b491-4507-8cdb-00e23de72ded': {
    type: 'secondary_insured_last_name'
  },
  '3a481a75-dcee-4061-8d80-3c09f30e4ac3': {
    type: 'secondary_insured_birthdate'
  },
  'cc141279-edad-4ff0-9b29-e6a9b7d1294b': {
    type: 'secondary_insured_ssn' 
  },
  '14a28b48-301c-426d-9e4c-964147eb1e58': {
    type: 'secondary_insurance_company_name'
  },
  'a96e8112-b6fd-4d6c-8afa-00afe585b845': {
    type: 'secondary_insurance_company_phone_number'
  },
  'cc572c71-ac6a-44b1-b0a9-c49fb882643d': {
    type: 'secondary_insurance_company_policy_number'
  },
  '85c01cc2-a0c6-41ec-a61a-e48e0cc2ec23': {
    type: 'secondary_insurance_company_group_number'
  },
  '31e43e88-b252-4d66-ae09-48405e7f6bfd': {
    type: 'secondary_insurance_company_address'
  },
  '5254687c-b173-4b4c-9cc9-1d18d827cd04': {
    type: 'secondary_insurance_company_city'
  },
  'b39ab428-6a96-4aaf-8f2f-15ead334f0fb': {
    type: 'secondary_insurance_company_state'
  },
  '289cc6f7-c96d-4f02-abef-117fbe58b378': {
    type: 'secondary_insurance_company_zip'
  },

  // Medical History
  '849e533e-987c-4416-846d-30855e2f0da0': {
    type: 'medical_history_treated'
  },
  'ef83f7a0-e677-4b63-a3ab-a85f21083fe2': {
    type: 'medical_history_treated_explanation'
  },
  'e2b0cf5a-99c8-4c15-9a98-4084f3d4edb5': {
    type: 'medical_history_major_operation'
  },
  'a819e530-3435-4bd8-bfb8-c4fae3c91254': {
    type: 'medical_history_major_operation_explanation'
  },
  '96d44488-172d-4adc-b718-b47176944ae2': {
    type: 'medical_history_injury'
  },
  '2c859d9f-ee49-4632-8b50-4cafee20590b': {
    type: 'medical_history_injury_explanation'
  },
  '51e4bdfa-22ef-4967-baf9-5baa2fded212': {
    type: 'medical_history_drugs'
  },
  '42948c7d-9685-4c05-b47b-25542d1d24ff': {
    type: 'medical_history_drugs_explanation'
  },
  '62ad18fd-88b6-4be1-8acf-9be87a270098': {
    type: 'medical_history_special_diet'
  },
  '557f1546-8b3c-483f-a82a-00286619c54b': {
    type: 'medical_history_special_diet_explanation'
  },
  'f1969adb-c48e-4e26-9230-6e12c077939c': {
    type: 'medical_history_tobacco'
  },
  'c4278bff-9968-4b43-958a-2866276362bb': {
    type: 'medical_history_tobacco_explanation'
  },
  '468af9c2-829a-4217-8c71-b4aa1cb34a86': {
    type: 'medical_history_antibiotics'
  },
  'eb395981-b9ae-4078-a6c5-5a288e66f6c0': {
    type: 'medical_history_antibiotics_explanation'
  },
  '452eca9f-d85c-4415-a21e-d24ca233f7c4': {
    type: 'medical_history_phenfen'
  },
  'c2937c53-8cb9-4921-9a5e-25b2716f2a45': {
    type: 'medical_history_phenfen_explanation'
  },
  'c40796e6-e29e-4c3b-8d86-e2b7d492c190': {
    type: 'medical_history_bisphosphonates'
  },
  '69df48bc-a8e5-4758-87ae-713f3059c42c': {
    type: 'medical_history_bisphosphonates_explanation'
  },
  '464ccd9a-e483-4a92-8924-dfb6d8e72de8': {
    type: 'medical_history_controlled'
  },
  'db0af17c-90c4-4742-9f8a-5d2deab45a17': {
    type: 'medical_history_controlled_explanation'
  },
  '0efc2786-4f87-4f75-8db3-5eb8e8790d13': {
    type: 'medical_history_alcohol'
  },
  '1e07ab0a-0310-4769-8b8e-142118e631a5': {
    type: 'medical_history_alcohol_explanation'
  },
  '11a59781-d1bf-401b-bddf-4e8ab38eab6b': {
    type: 'medical_history_pregnancy'
  },
  '4cd7124e-9ce5-475f-92c8-e0aa76d4daed': {
    type: 'medical_history_allergies'
  },
  'eddf20a6-faa0-4c5c-9375-bb2e468bee42': {
    type: 'medical_history_conditions'
  },
  'ab7552a5-0781-4f14-b63b-60ffd120d6cf': {
    type: 'medical_history_other_explanation'
  },

  // Dental History, picklist support to be added as future enhancement
  // '058a4fb1-2d51-4740-b33a-ca7f388d0da1': {
  //   type: 'dental_history_feeling_picklist'
  // },
  'ed6bbfb1-5edc-491f-905c-ea7754607a30': {
    type: 'dental_history_seen_dentist'
  },
  // '5727f43e-1cc1-4aa8-b6fc-ed3070c22d52': {
  //   type: 'dental_history_last_visit_picklist'
  // },
  // '4f688ed7-3a7e-47ea-961b-bc1f2749d7c2': {
  //   type: 'dental_history_rating_picklist'
  // },
  '4a0a9979-0c0b-4831-89bd-150f17ca9239': {
    type: 'dental_history_rating_concerns'
  },
  '1d9ed086-d6dd-4061-9c81-4b7b40d78d18': {
    type: 'dental_history_avoid'
  },
  'aa1446c5-d059-4208-bd17-683441e78aec': {
    type: 'dental_history_avoid_explanation'
  },
  '3741d5ad-5b38-401b-b140-cb3be9cd7e98': {
    type: 'dental_history_appearance'
  },
  'fb32d139-11a4-46dc-8cdf-a1d28feb0654': {
    type: 'dental_history_appearance_explanation'
  },
  // '43fe42a0-c95a-4782-9d4c-3eb2454973c4': {
  //   type: 'dental_history_brush_picklist'
  // },
  // 'b4e23589-8eaa-4a6b-bbef-3190169c03fa': {
  //   type: 'dental_history_floss_picklist'
  // },
  // 'How often do you use other aids?': {
  //   type: 'dental_history_other_aids_picklist'
  // },
  'ce4ff736-f37f-49ea-a19a-295ec903369c': {
    type: 'dental_history_whiter_teeth'
  },
  'a9a9c0f5-07e3-43a5-8417-4961ddc29a81': {
    type: 'dental_history_straighter_teeth'
  },
  '55bf5db1-71dc-47c6-b1de-1a823a89451d': {
    type: 'dental_history_conditions'
  },
  '27c2c39e-6399-4088-85ea-c360f5f26c2f': {
    type: 'dental_history_other_office_name'
  },
  'b96e52ca-a11a-4b8f-924f-e09ec9744ae8': {
    type: 'dental_history_other_office_city'
  },
  // '2bb82326-2349-4f9c-b834-cad76b4d6be0': {
  //   type: 'dental_history_other_office_state'
  // }
};

const setFieldValue = (field, path, userInfo) => {
  const value = pathOr('', path)(userInfo);
  if (value) {
    field.value = value;
  }
};

const getFieldValueMapper = (userInfo) => ({
  // Basic information
  name_first: (field) => setFieldValue(field, ['name', 'nameFirst'], userInfo),
  name_last: (field) => setFieldValue(field, ['name', 'nameLast'], userInfo),
  name_middle: (field) => setFieldValue(field, ['name', 'nameMiddle'], userInfo),
  email: (field) => setFieldValue(field, ['emailAddress'], userInfo),
  primary_phone: (field) => setFieldValue(field, ['phoneNumbers', 0, 'number'], userInfo),
  alt_phone: (field) => setFieldValue(field, ['phoneNumbers', 1, 'number'], userInfo),
  birthdate: (field) => setFieldValue(field, ['birthDate'], userInfo),
  ssn: (field) => setFieldValue(field, ['ssn'], userInfo),
  sex: (field) => {
    const sexValue = pathOr('', ['gender'])(userInfo);
    if (sexValue) {
      field.value = capitalizeFirstLetter(sexValue);
    }
  },
  address: (field) => setFieldValue(field, ['addressInformation', 'address', 'addressLine1'], userInfo),
  address2: (field) => setFieldValue(field, ['addressInformation', 'address', 'addressLine2'], userInfo),
  city: (field) => setFieldValue(field, ['addressInformation', 'address', 'city'], userInfo),
  state: (field) => setFieldValue(field, ['addressInformation', 'address', 'state'], userInfo),
  zip: (field) => setFieldValue(field, ['addressInformation', 'address', 'postalCode'], userInfo),
  marital_status: (field) => setFieldValue(field, ['marital_status'], userInfo),
  communication_type_preference: (field) => {
    const communicationTypePreferenceValue = pathOr({}, [
      'communication_type_preference'
    ])(userInfo);

    if (!isEmpty(communicationTypePreferenceValue)) {
      field.value = Object.keys(communicationTypePreferenceValue).reduce(
        (acc, k) => {
          if (k === 'email_and_text') {
            acc.push({ 'Email and Text': communicationTypePreferenceValue[k] });
          }

          if (k === 'email_only') {
            acc.push({ 'Email Only': communicationTypePreferenceValue[k] });
          }

          if (k === 'text_only') {
            acc.push({ 'Text Only': communicationTypePreferenceValue[k] });
          }

          if (k === 'none') {
            acc.push({ None: communicationTypePreferenceValue[k] });
          }

          return acc;
        },
        [] as { [key: string]: boolean }[]
      );
    } else {
      const defaultValue = [
        { 'Email and Text': false },
        { 'Email Only': false },
        { 'Text Only': false },
        { None: false }
      ];

      field.value = defaultValue;
    }
  },

  // Spouse information
  spouse_first_name: (field) => setFieldValue(field, ['spouse', 'nameFirst'], userInfo),
  spouse_middle_name: (field) => setFieldValue(field, ['spouse', 'nameMiddle'], userInfo),
  spouse_last_name: (field) => setFieldValue(field, ['spouse', 'nameLast'], userInfo),
  spouse_birthdate: (field) => setFieldValue(field, ['spouse', 'birthdate'], userInfo),
  spouse_ssn: (field) => setFieldValue(field, ['spouse', 'ssn'], userInfo),
  spouse_phone: (field) => setFieldValue(field, ['spouse', 'phoneNumbers', 0, 'number'], userInfo),

  // Guardian 1 information
  guardian_1_first_name: (field) => setFieldValue(field, ['guardian_1', 'nameFirst'], userInfo),
  guardian_1_middle_name: (field) => setFieldValue(field, ['guardian_1', 'nameMiddle'], userInfo),
  guardian_1_last_name: (field) => setFieldValue(field, ['guardian_1', 'nameLast'], userInfo),
  guardian_1_relationship: (field) => setFieldValue(field, ['guardian_1', 'relationship'], userInfo),
  guardian_1_birthdate: (field) => setFieldValue(field, ['guardian_1', 'birthdate'], userInfo),
  guardian_1_ssn: (field) => setFieldValue(field, ['guardian_1', 'ssn'], userInfo),
  guardian_1_sex: (field) => {
    const sexValue = pathOr('', ['guardian_1', 'gender'])(userInfo);
    if (sexValue) {
      field.value = capitalizeFirstLetter(sexValue);
    }
  },
  guardian_1_email: (field) => setFieldValue(field, ['guardian_1', 'emailAddress'], userInfo),
  guardian_1_primary_phone: (field) => setFieldValue(field, ['guardian_1', 'phoneNumbers', 0, 'number'], userInfo),
  guardian_1_alt_phone: (field) => setFieldValue(field, ['guardian_1', 'phoneNumbers', 1, 'number'], userInfo),
  guardian_1_address: (field) => setFieldValue(field, ['guardian_1', 'address', 'street'], userInfo),
  guardian_1_address2: (field) => setFieldValue(field, ['guardian_1', 'address', 'street2'], userInfo),
  guardian_1_city: (field) => setFieldValue(field, ['guardian_1', 'address', 'city'], userInfo),
  guardian_1_state: (field) => setFieldValue(field, ['guardian_1', 'address', 'state'], userInfo),
  guardian_1_zip: (field) => setFieldValue(field, ['guardian_1', 'address', 'zipcode'], userInfo),
  guardian_1_country: (field) => setFieldValue(field, ['guardian_1', 'address', 'country'], userInfo),

  // Guardian 2 information
  guardian_2_first_name: (field) => setFieldValue(field, ['guardian_2', 'nameFirst'], userInfo),
  guardian_2_middle_name: (field) => setFieldValue(field, ['guardian_2', 'nameMiddle'], userInfo),
  guardian_2_last_name: (field) => setFieldValue(field, ['guardian_2', 'nameLast'], userInfo),
  guardian_2_relationship: (field) => setFieldValue(field, ['guardian_2', 'relationship'], userInfo),
  guardian_2_birthdate: (field) => setFieldValue(field, ['guardian_2', 'birthdate'], userInfo),
  guardian_2_ssn: (field) => setFieldValue(field, ['guardian_2', 'ssn'], userInfo),
  guardian_2_sex: (field) => {
    const sexValue = pathOr('', ['guardian_2', 'gender'])(userInfo);
    if (sexValue) {
      field.value = capitalizeFirstLetter(sexValue);
    }
  },
  guardian_2_email: (field) => setFieldValue(field, ['guardian_2', 'emailAddress'], userInfo),
  guardian_2_primary_phone: (field) => setFieldValue(field, ['guardian_2', 'phoneNumbers', 0, 'number'], userInfo),
  guardian_2_alt_phone: (field) => setFieldValue(field, ['guardian_2', 'phoneNumbers', 1, 'number'], userInfo),
  guardian_2_address: (field) => setFieldValue(field, ['guardian_2', 'address', 'street'], userInfo),
  guardian_2_address2: (field) => setFieldValue(field, ['guardian_2', 'address', 'street2'], userInfo),
  guardian_2_city: (field) => setFieldValue(field, ['guardian_2', 'address', 'city'], userInfo),
  guardian_2_state: (field) => setFieldValue(field, ['guardian_2', 'address', 'state'], userInfo),
  guardian_2_zip: (field) => setFieldValue(field, ['guardian_2', 'address', 'zipcode'], userInfo),
  guardian_2_country: (field) => setFieldValue(field, ['guardian_2', 'address', 'country'], userInfo),

  // Billing information
  billing_party_relationship_to_patient: (field) => setFieldValue(field, ['billing', 'relationship'], userInfo),
  billing_first_name: (field) => setFieldValue(field, ['billing', 'nameFirst'], userInfo),
  billing_middle_name: (field) => setFieldValue(field, ['billing', 'nameMiddle'], userInfo),
  billing_last_name: (field) => setFieldValue(field, ['billing', 'nameLast'], userInfo),
  billing_birthdate: (field) => setFieldValue(field, ['billing', 'birthdate'], userInfo),
  billing_ssn: (field) => setFieldValue(field, ['billing', 'ssn'], userInfo),
  billing_sex: (field) => {
    const sexValue = pathOr('', ['billing', 'gender'])(userInfo);
    if (sexValue) {
      field.value = capitalizeFirstLetter(sexValue);
    }
  },
  billing_email: (field) => setFieldValue(field, ['billing', 'emailAddress'], userInfo),
  billing_address: (field) => setFieldValue(field, ['billing', 'address', 'street'], userInfo),
  billing_address2: (field) => setFieldValue(field, ['billing', 'address', 'street2'], userInfo),
  billing_city: (field) => setFieldValue(field, ['billing', 'address', 'city'], userInfo),
  billing_state: (field) => setFieldValue(field, ['billing', 'address', 'state'], userInfo),
  billing_zip: (field) => setFieldValue(field, ['billing', 'address', 'zipcode'], userInfo),
  billing_country: (field) => setFieldValue(field, ['billing', 'address', 'country'], userInfo),
  billing_primary_phone: (field) => setFieldValue(field, ['billing', 'phoneNumbers', 0, 'number'], userInfo),
  billing_alt_phone: (field) => setFieldValue(field, ['billing', 'phoneNumbers', 1, 'number'], userInfo),
  how_did_you_hear_about_us: (field) => setFieldValue(field, ['referral_type'], userInfo),

  // Primary Insurance
  primary_insured_first_name: (field) => setFieldValue(field, ['primaryInsurance', 'name_first'], userInfo),
  primary_insured_last_name: (field) => setFieldValue(field, ['primaryInsurance', 'name_last'], userInfo),
  primary_insured_birthdate: (field) => setFieldValue(field, ['primaryInsurance', 'birthdate'], userInfo),
  primary_insured_ssn: (field) => setFieldValue(field, ['primaryInsurance', 'ssn'], userInfo),
  primary_insurance_company_name: (field) => setFieldValue(field, ['primaryInsurance', 'company_name'], userInfo),
  primary_insurance_company_phone_number: (field) => setFieldValue(field, ['primaryInsurance', 'company_phone_number'], userInfo),
  primary_insurance_company_policy_number: (field) => setFieldValue(field, ['primaryInsurance', 'company_policy_number'], userInfo),
  primary_insurance_company_group_number: (field) => setFieldValue(field, ['primaryInsurance', 'company_group_number'], userInfo),
  primary_insurance_company_address: (field) => setFieldValue(field, ['primaryInsurance', 'company_address1'], userInfo),
  primary_insurance_company_city: (field) => setFieldValue(field, ['primaryInsurance', 'company_city'], userInfo),
  primary_insurance_company_state: (field) => setFieldValue(field, ['primaryInsurance', 'company_state'], userInfo),
  primary_insurance_company_zip: (field) => setFieldValue(field, ['primaryInsurance', 'company_zip'], userInfo),

  // Secondary Insurance
  secondary_insured_first_name: (field) => setFieldValue(field, ['secondaryInsurance', 'name_first'], userInfo),
  secondary_insured_last_name: (field) => setFieldValue(field, ['secondaryInsurance', 'name_last'], userInfo),
  secondary_insured_birthdate: (field) => setFieldValue(field, ['secondaryInsurance', 'birthdate'], userInfo),
  secondary_insured_ssn: (field) => setFieldValue(field, ['secondaryInsurance', 'ssn'], userInfo),
  secondary_insurance_company_name: (field) => setFieldValue(field, ['secondaryInsurance', 'company_name'], userInfo),
  secondary_insurance_company_phone_number: (field) => setFieldValue(field, ['secondaryInsurance', 'company_phone_number'], userInfo),
  secondary_insurance_company_policy_number: (field) => setFieldValue(field, ['secondaryInsurance', 'company_policy_number'], userInfo),
  secondary_insurance_company_group_number: (field) => setFieldValue(field, ['secondaryInsurance', 'company_group_number'], userInfo),
  secondary_insurance_company_address: (field) => setFieldValue(field, ['secondaryInsurance', 'company_address1'], userInfo),
  secondary_insurance_company_city: (field) => setFieldValue(field, ['secondaryInsurance', 'company_city'], userInfo),
  secondary_insurance_company_state: (field) => setFieldValue(field, ['secondaryInsurance', 'company_state'], userInfo),
  secondary_insurance_company_zip: (field) => setFieldValue(field, ['secondaryInsurance', 'company_zip'], userInfo),

  // Medical History
  medical_history_treated: (field) => setFieldValue(field, ['medical', 'Are you currently being treated by a physician for a specific condition?'], userInfo),
  medical_history_treated_explanation: (field) => setFieldValue(field, ['medical', 'If so, please tell us about your treatment'], userInfo),
  medical_history_major_operation: (field) => setFieldValue(field, ['medical', 'Have you recently been hospitalized or had a major operation?'], userInfo),
  medical_history_major_operation_explanation: (field) => setFieldValue(field, ['medical', 'If so, please tell us about the hospitalization'], userInfo),
  medical_history_injury: (field) => setFieldValue(field, ['medical', 'Have you ever had a serious head or neck injury?'], userInfo),
  medical_history_injury_explanation: (field) => setFieldValue(field, ['medical', 'If so, please tell us about the head/neck injury'], userInfo),
  medical_history_drugs: (field) => setFieldValue(field, ['medical', 'Are you taking any medications, pills, or drugs?'], userInfo),
  medical_history_drugs_explanation: (field) => setFieldValue(field, ['medical', 'Please list all medications and dosage'], userInfo),
  medical_history_special_diet: (field) => setFieldValue(field, ['medical', 'Are you on a special diet?'], userInfo),
  medical_history_special_diet_explanation: (field) => setFieldValue(field, ['medical', 'Please tell us about your diet'], userInfo),
  medical_history_tobacco: (field) => setFieldValue(field, ['medical', 'Do you use tobacco?'], userInfo),
  medical_history_tobacco_explanation: (field) => setFieldValue(field, ['medical', 'Please tell us how often and what type of tobacco consumption'], userInfo),
  medical_history_antibiotics: (field) => setFieldValue(field, ['medical', 'Have you ever been advised that you require antibiotics prior to a dental appointment?'], userInfo),
  medical_history_antibiotics_explanation: (field) => setFieldValue(field, ['medical', 'Please tell us about the antibiotics'], userInfo),
  medical_history_phenfen: (field) => setFieldValue(field, ['medical', 'Do you take, or have you taken, PhenFen or Redux?'], userInfo),
  medical_history_phenfen_explanation: (field) => setFieldValue(field, ['medical', 'If so, please tell us about your PhenFen/Redux usage'], userInfo),
  medical_history_bisphosphonates: (field) => setFieldValue(field, ['medical', 'Have you ever taken Fosomax, Boniva, Actonel or any other medications containing bisphosphonates?'], userInfo),
  medical_history_bisphosphonates_explanation: (field) => setFieldValue(field, ['medical', 'If so, please tell us about your bisphosphonate usage'], userInfo),
  medical_history_controlled: (field) => setFieldValue(field, ['medical', 'Have you recently used controlled substances?'], userInfo),
  medical_history_controlled_explanation: (field) => setFieldValue(field, ['medical', 'If so, please tell us which controlled substances and amount/frequency'], userInfo),
  medical_history_alcohol: (field) => setFieldValue(field, ['medical', 'Have you recently consumed alcohol?'], userInfo),
  medical_history_alcohol_explanation: (field) => setFieldValue(field, ['medical', 'Please tell how much alcohol and how recently'], userInfo),
  medical_history_other_explanation: (field) => setFieldValue(field, ['medical', 'Do you have any condition or problem, not listed, which we should know about? Please explain'], userInfo),
  medical_history_pregnancy: (field) => {
    field.value = getAlertEntries(userInfo, MED_CONSTANT.PREGNANCY_OPTIONS);
  },
  medical_history_allergies: (field) => {
    field.value = getAlertEntries(userInfo, MED_CONSTANT.ALLERGY_OPTIONS);
  },
  medical_history_conditions: (field) => {
    field.value = getAlertEntries(userInfo, MED_CONSTANT.MED_CONDITION_OPTIONS);
  },

  // Dental History
  dental_history_seen_dentist: (field) => setFieldValue(field, ['medical', 'Have you seen a dentist before?'], userInfo),
  dental_history_rating_concerns: (field) => setFieldValue(field, ['medical', 'What are your dental concerns?'], userInfo),
  dental_history_avoid: (field) => setFieldValue(field, ['medical', 'Have you avoided regular dental care?'], userInfo),
  dental_history_avoid_explanation: (field) => setFieldValue(field, ['medical', 'If so, why have you avoided regular dental care?'], userInfo),
  dental_history_appearance: (field) => setFieldValue(field, ['medical', 'Are you happy with the appearance of your teeth?'], userInfo),
  dental_history_appearance_explanation: (field) => setFieldValue(field, ['medical', 'If not, why are you unhappy with the appearance of your teeth?'], userInfo),
  dental_history_whiter_teeth: (field) => setFieldValue(field, ['medical', 'Would you like your teeth to be whiter?'], userInfo),
  dental_history_straighter_teeth: (field) => setFieldValue(field, ['medical', 'Would you like your teeth to be straighter?'], userInfo),
  dental_history_other_office_name: (field) => setFieldValue(field, ['medical', 'Name of previous dentist or dental office'], userInfo),
  dental_history_other_office_city: (field) => setFieldValue(field, ['medical', 'City'], userInfo),
  dental_history_conditions: (field) => {
    field.value = getAlertEntries(userInfo, MED_CONSTANT.DENTAL_CONDITION_OPTIONS);
  },
  // dental_history_feeling_picklist: (field) => {
  //   field.value = pathOr('', [
  //     'medical',
  //     'How do you feel about dental treatment?'
  //   ])(userInfo);
  // },
  // dental_history_last_visit_picklist: (field) => {
  //   field.value = pathOr('', [
  //     'medical',
  //     'If so, when was your last dental visit?'
  //   ])(userInfo);
  // },
  // dental_history_rating_picklist: (field) => {
  //   field.value = pathOr('', [
  //     'medical',
  //     'How would you rate your previous dental experience?'
  //   ])(userInfo);
  // },
  // dental_history_brush_picklist: (field) => {
  //   field.value = pathOr('', [
  //     'medical',
  //     'How often do you brush?'
  //   ])(userInfo);
  // },
  // dental_history_floss_picklist: (field) => {
  //   field.value = pathOr('', [
  //     'medical',
  //     'How often do you floss?'
  //   ])(userInfo);
  // },
  // dental_history_other_aids_picklist: (field) => {
  //   field.value = pathOr('', [
  //     'medical',
  //     'How often do you use other aids?'
  //   ])(userInfo);
  // },
  // dental_history_other_office_state: (field) => {
  //   var region = pathOr('', [
  //     'medical',
  //     'State / Province'
  //   ])(userInfo);

  //   if (region) {
  //     field.value = MED_CONSTANT.REGION_ACRONYMS[region]
  //   }
  // },

  [CONTACT_FIELD_NAME_FIRST]: (field) => setFieldValue(field, ['name', 'nameFirst'], userInfo),
  [CONTACT_FIELD_NAME_LAST]: (field) => setFieldValue(field, ['name', 'nameLast'], userInfo),
  [CONTACT_FIELD_NAME_MIDDLE]: (field) => setFieldValue(field, ['name', 'nameMiddle'], userInfo),
  [CONTACT_FIELD_ADDRESS]: (field) => setFieldValue(field, ['emailAddress'], userInfo),
  [CONTACT_FIELD_PHONE]: (field) => setFieldValue(field, ['phoneNumbers', 0, 'number'], userInfo),
  [CONTACT_FIELD_BIRTHDATE]: (field) => setFieldValue(field, ['birthDate'], userInfo),
});

export {
  LIST_BASIC_FIELD_TYPE,
  LIST_PREPOPULATE_FIELDS_FOR_PATIENT_FORMS,
  PATIENT_FORMS,
  CONTACT_FIELDS,
  getFieldValueMapper,
  getBasicFieldType,
  canPrepopulateInInitialPatientForm,
  canPrepopulateInOtherForms,
  isBasicField
};
